import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, User, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../../utils/Images";
import { useDispatch, useSelector } from "react-redux";
import { clearUser } from "../../redux/userSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const Navbar = () => {
  const { txtconnect_ng_user } = useSelector((state) => state.user);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(clearUser());
    toast.success("Logged out successfully");
    navigate("/");
  };

  //* Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  //* Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setMoreDropdownOpen(false);
        setReportsDropdownOpen(false);
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setMoreDropdownOpen(false);
    setReportsDropdownOpen(false);
    setProfileDropdownOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: "Dashboard", path: "/home" },
    { name: "Contacts", path: "/contact-group" },
    { name: "Quick Messages", path: "/quick-sms" },
    { name: "Bulk Messages", path: "/bulk-sms" },
            { name:"Message Reports", path:"/message-reports" },
          
    // {
    //   name: "Reports",
    //   path: "/reports",
    //   hasDropdown: true,
    //   dropdownItems: [
    //     { name: "Bulk SMS Report", path: "/reports/bulk-sms" },
    //     { name: "Quick SMS Report", path: "/reports/quick-sms" },
    //     { name: "Search Phone Number", path: "/reports/search-phone" },
    //   ],
    // },
    {
      name: "More",
      path: "#",
      hasDropdown: true,
      dropdownItems: [
        { name: "Buy SMS Package", path: "/subscription-plans" },
        { name: "SenderID Management", path: "/senders" },
        { name: "API Key", path: "/api-key" },
        {name: "Contact Us", path:"/contact-us" },
          {name:"Scheduled Messages",path:"/scheduled-messages"},
        {name:"Payment History", path:"/payment-history" },
        { name: "Birthdays", path: "/birthdays" }
        
      ],
    },
  ];

  //* Determine if a nav item is active
  const isActive = (path) => {
    if (path === "#") return false;
    return location.pathname === path || location.pathname.startsWith(path);
  };

  //* Handle dropdown toggling
  const toggleDropdown = (dropdown, event) => {
    if (event) {
      event.stopPropagation();
    }

    if (dropdown === "more") {
      setMoreDropdownOpen(!moreDropdownOpen);
      setReportsDropdownOpen(false);
    } else if (dropdown === "reports") {
      setReportsDropdownOpen(!reportsDropdownOpen);
      setMoreDropdownOpen(false);
    }
  };

  //* Animation variants
  const navbarVariants = {
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
    hidden: {
      opacity: 0,
      y: -25,
      transition: {
        duration: 0.3,
      },
    },
  };

  //* Dropdown variants
  const dropdownVariants = {
    open: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
        duration: 0.2,
      },
    },
    closed: {
      opacity: 0,
      y: -10,
      transition: {
        duration: 0.15,
      },
    },
  };

  //* Mobile menu variants
  const mobileMenuVariants = {
    open: {
      opacity: 1,
      height: "auto",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
        staggerChildren: 0.05,
      },
    },
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.2,
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  };

  //* Menu item variants
  const menuItemVariants = {
    open: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.2,
      },
    },
    closed: {
      opacity: 0,
      y: 20,
      transition: {
        duration: 0.2,
      },
    },
  };

  return (
    <motion.nav
      initial="visible"
      animate="visible"
      variants={navbarVariants}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "py-4 bg-white shadow-md"
          : "py-4 backdrop-blur-sm bg-white/95"
      }`}
    >
      <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/dashboard">
              <img
                src={Images.logo}
                alt="TXTConnect"
                className="w-auto h-8 md:h-10"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-2 lg:space-x-4">
            {navItems.map((item) => (
              <div key={item.name} className="relative dropdown-container">
                {item.hasDropdown ? (
                  <motion.button
                    onClick={(e) => toggleDropdown(item.name.toLowerCase(), e)}
                    className={`flex items-center text-sm lg:text-sm font-medium px-2 py-2 rounded-md transition-colors hover:text-red-600 ${
                      isActive(item.path || "/")
                        ? "text-red-600"
                        : "text-gray-700"
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {item.name}
                    <motion.div
                      animate={{
                        rotate:
                          (item.name === "Reports" && reportsDropdownOpen) ||
                          (item.name === "More" && moreDropdownOpen)
                            ? 180
                            : 0,
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown size={16} className="ml-1" />
                    </motion.div>
                  </motion.button>
                ) : (
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to={item.path}
                      className={`text-sm lg:text-sm font-medium px-2 py-2 rounded-md transition-colors hover:text-red-600 ${
                        isActive(item.path) ? "text-red-600" : "text-gray-700"
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                )}

                {/* Dropdown for Reports */}
                <AnimatePresence>
                  {item.name === "Reports" && reportsDropdownOpen && (
                    <motion.div
                      className="overflow-hidden absolute right-0 z-20 mt-2 w-56 bg-white rounded-md border border-gray-100 shadow-lg"
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={dropdownVariants}
                    >
                      <div className="py-1">
                        {item.dropdownItems.map((dropdownItem) => (
                          <motion.div
                            key={dropdownItem.name}
                            whileHover={{ backgroundColor: "#f9fafb", x: 5 }}
                            transition={{ duration: 0.1 }}
                          >
                            <Link
                              to={dropdownItem.path}
                              className="block px-4 py-3 text-sm text-gray-700 transition-colors hover:text-red-600"
                            >
                              {dropdownItem.name}
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Dropdown for More */}
                <AnimatePresence>
                  {item.name === "More" && moreDropdownOpen && (
                    <motion.div
                      className="overflow-hidden absolute right-0 z-20 mt-2 w-56 bg-white rounded-md border border-gray-100 shadow-lg"
                      initial="closed"
                      animate="open"
                      exit="closed"
                      variants={dropdownVariants}
                    >
                      <div className="py-1">
                        {item.dropdownItems.map((dropdownItem) => (
                          <motion.div
                            key={dropdownItem.name}
                            whileHover={{ backgroundColor: "#f9fafb", x: 5 }}
                            transition={{ duration: 0.1 }}
                          >
                            <Link
                              to={dropdownItem.path}
                              className="block px-4 py-3 text-sm text-gray-700 transition-colors hover:text-red-600"
                            >
                              {dropdownItem.name}
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* User actions area */}
          <div className="hidden items-center space-x-4 md:flex">
            <div className="relative dropdown-container">
              <motion.button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center space-x-2 text-gray-700 transition-colors hover:text-red-600"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="flex justify-center items-center w-8 h-8 bg-red-100 rounded-full">
                  <User size={18} className="text-red-600" />
                </div>
                <motion.div
                  animate={{ rotate: profileDropdownOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown size={16} className="hidden lg:block" />
                </motion.div>
              </motion.button>
              <AnimatePresence>
                {profileDropdownOpen && (
                  <motion.div
                    className="overflow-hidden absolute right-0 z-20 mt-2 w-48 bg-white rounded-md border border-gray-100 shadow-lg"
                    initial="closed"
                    animate="open"
                    exit="closed"
                    variants={dropdownVariants}
                  >
                    <div className="py-1">
                      <motion.div
                        whileHover={{ backgroundColor: "#f9fafb", x: 5 }}
                        transition={{ duration: 0.1 }}
                      >
                        <Link
                          to="/profile"
                          className="block px-4 py-2 text-sm text-gray-700 transition-colors hover:text-red-600"
                        >
                          Your Profile
                        </Link>
                      </motion.div>
                      <div className="border-t border-gray-100"></div>
                      <motion.div
                        whileHover={{ backgroundColor: "#f9fafb", x: 5 }}
                        transition={{ duration: 0.1 }}
                      >
                        <button
                          className="block px-4 py-2 w-full text-sm text-left text-red-600 transition-colors hover:bg-gray-50"
                          onClick={handleLogout}
                        >
                          <div className="flex items-center">
                            <LogOut size={16} className="mr-2" />
                            <span>Sign out</span>
                          </div>
                        </button>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex justify-center items-center p-2 text-gray-700 rounded-md hover:text-red-600 hover:bg-gray-100 focus:outline-none"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            className="overflow-hidden md:hidden"
            initial="closed"
            animate="open"
            exit="closed"
            variants={mobileMenuVariants}
          >
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white rounded-b-xl shadow-lg">
              {navItems.map((item) => (
                <motion.div key={item.name} variants={menuItemVariants}>
                  {item.hasDropdown ? (
                    <>
                      <motion.button
                        onClick={(e) =>
                          toggleDropdown(item.name.toLowerCase(), e)
                        }
                        className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-md text-base font-medium ${
                          isActive(item.path)
                            ? "text-red-600 bg-red-50"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                        whileTap={{ scale: 0.98 }}
                      >
                        {item.name}
                        <motion.div
                          animate={{
                            rotate:
                              (item.name === "Reports" &&
                                reportsDropdownOpen) ||
                              (item.name === "More" && moreDropdownOpen)
                                ? 180
                                : 0,
                          }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown size={16} />
                        </motion.div>
                      </motion.button>

                      {/* Mobile Reports Dropdown */}
                      <AnimatePresence>
                        {item.name === "Reports" && reportsDropdownOpen && (
                          <motion.div
                            className="pl-4 mt-1 space-y-1"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            {item.dropdownItems.map((dropdownItem, idx) => (
                              <motion.div
                                key={dropdownItem.name}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                              >
                                <Link
                                  to={dropdownItem.path}
                                  className="block px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-red-600"
                                >
                                  {dropdownItem.name}
                                </Link>
                              </motion.div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Mobile More Dropdown */}
                      <AnimatePresence>
                        {item.name === "More" && moreDropdownOpen && (
                          <motion.div
                            className="pl-4 mt-1 space-y-1"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            {item.dropdownItems.map((dropdownItem, idx) => (
                              <motion.div
                                key={dropdownItem.name}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                              >
                                <Link
                                  to={dropdownItem.path}
                                  className="block px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-red-600"
                                >
                                  {dropdownItem.name}
                                </Link>
                              </motion.div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <motion.div
                      whileHover={{ x: 5 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Link
                        to={item.path}
                        className={`block px-3 py-2 rounded-md text-base font-medium ${
                          isActive(item.path)
                            ? "text-red-600 bg-red-50"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  )}
                </motion.div>
              ))}

              {/* Mobile user actions */}
              <motion.div
                className="pt-4 pb-3 border-t border-gray-200"
                variants={menuItemVariants}
              >
                <div className="flex items-center px-3">
                  <div className="flex-shrink-0">
                    <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full">
                      <User size={20} className="text-red-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-800 truncate">
                      {txtconnect_ng_user?.full_name}
                    </div>
                    <div className="text-sm font-medium text-gray-500 truncate">
                      {txtconnect_ng_user?.email}
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <motion.div whileHover={{ x: 5 }} whileTap={{ scale: 0.98 }}>
                    <Link
                      to="/profile"
                      className="block px-3 py-2 text-base font-medium text-gray-700 rounded-md hover:bg-gray-50 hover:text-red-600"
                    >
                      Your Profile
                    </Link>
                  </motion.div>

                  <motion.div whileHover={{ x: 5 }} whileTap={{ scale: 0.98 }}>
                    <button
                      onClick={handleLogout}
                      className="block px-3 py-2 w-full text-base font-medium text-left text-red-600 rounded-md hover:bg-gray-50"
                    >
                      <div className="flex items-center">
                        <LogOut size={18} className="mr-2" />
                        <span>Sign out</span>
                      </div>
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
